/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'
import scopesImg from 'assets/images/scopes.png'

export const _12 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>1 - Область видимости</h2>
    <p className={styles.description}>
      <b>Область видимости переменных (variable scope)</b> - доступность переменных в определенном месте кода. Есть
      несколько областей видимости: <code className={styles.code}>глобальная</code>,
      <code className={styles.code}>блочная</code>, <code className={styles.code}>eval</code> и{' '}
      <code className={styles.code}>функции</code>.
    </p>
    <p className={styles.description}>
      Глобальная область видимости используется по умолчанию. Все и вся имеют доступ к переменным объявленным в ней.
      Переменные объявленные в глобальной области видимости уязвимы, так как их может изменить любой участок кода.
    </p>
    <p className={styles.description}>
      Рассмотрим на примере. Переменная <code className={styles.code}>value</code> объявлена в глобальной области
      видимости, то есть вне какого-то блока, и доступна в любом месте после объявления.
    </p>
    <CopyBlock
      text={`const value = 5;

if (true) {
  console.log('Block scope: ', value); // 5
}

console.log('Global scope: ', value); // 5`}
      language="js"
      showLineNumbers={false}
      theme={ocean}
    />

    <p className={styles.description}>
      Любая конструкция использующая фигурные скобки <code className={styles.code}>{'{}'}</code> (условия, циклы,
      функции и т. п.) создает новую локальную область видимости, и переменные, объявленные в этой области видимости,
      используя <code className={styles.code}>let</code> или <code className={styles.code}>const</code>, не доступны вне
      этого блока.
    </p>
    <CopyBlock
      text={`if (true) {
  const value = 5;
  console.log('Block scope: ', value); // 5
}

console.log('Global scope: ', value); // ReferenceError: value is not defined`}
      language="js"
      showLineNumbers={false}
      theme={ocean}
    />

    <p className={styles.description}>
      Глубина вложенности областей видимости не ограничена, и все они буду работать по одному принципу - область
      видимости имеет доступ ко всем переменным объявленным выше по иерархии вложенности, но не может получить доступ к
      переменным объявленным во вложенных областях видимости.
    </p>
    <p className={styles.description}>Создадим несколько областей видимости и дадим им имена для наглядности.</p>
    <img className={styles.img} src={scopesImg} alt="scopes" />
    <ul className={styles.list}>
      <li className={styles.list__item}>
        Глобальная есть по умолчанию, создадим в ней переменную <code className={styles.code}>global</code>
      </li>
      <li className={styles.list__item}>
        Далее используя оператор <code className={styles.code}>if</code> создадим блочную область видимости{' '}
        <code className={styles.code}>block A</code>
      </li>
      <li className={styles.list__item}>
        Внутри области видимости <code className={styles.code}>block A</code> поставим еще один оператор{' '}
        <code className={styles.code}>if</code>, который создаст вложенную область видимости{' '}
        <code className={styles.code}>block B</code>
      </li>
      <li className={styles.list__item}>
        На одном уровне с <code className={styles.code}>block A</code>, создадим область видимости{' '}
        <code className={styles.code}>block C</code> все так же используя оператор{' '}
        <code className={styles.code}>if</code>
      </li>
    </ul>
    <CopyBlock
      text={`const global = 'global';

if (true) {
  const blockA = 'block A';

  // Видим глобальную + локальную A
  console.log(global); // 'global'
  console.log(blockA); // block A

  /*
   * Переменные blockB и blockC не найдены в доступных областях видимости.
   * Будет ошибка обращения к переменной.
   */
  console.log(blockB); // ReferenceError: blockB is not defined
  console.log(blockC); // ReferenceError: blockC is not defined

  if (true) {
    const blockB = 'block B';

    // Видим глобальную + внешнюю A + локальную B
    console.log(global); // global
    console.log(blockA); // block A
    console.log(blockB); // block B

    /*
     * Переменная blockC не найдена в доступных областях видимости.
     * Будет ошибка обращения к переменной.
     */
    console.log(blockC); // ReferenceError: blockC is not defined
  }
}

if (true) {
  const blockC = 'block C';

  // Видим глобальную + локальную C
  console.log(global); // global
  console.log(blockC); // block C

  /*
   * Переменные blockA и blockB не найдены в доступных областях видимости.
   * Будет ошибка обращения к переменной.
   */
  console.log(blockA); // ReferenceError: blockA is not defined
  console.log(blockB); // ReferenceError: blockB is not defined
}

// Видим только глобальную
console.log(global); // global

/*
 * Переменные blockA, blockB и blockC не найдены в доступных областях видимости.
 * Будет ошибка обращения к переменной.
 */
console.log(blockA); // ReferenceError: blockA is not defined
console.log(blockB); // ReferenceError: blockB is not defined
console.log(blockC); // ReferenceError: blockC is not defined`}
      language="js"
      showLineNumbers={false}
      theme={ocean}
    />

    <p className={styles.notificationStudy}>
      Будьте внимательны при использовании блочных областей видимости и переменных объявленных в них. Именно эта ошибка,
      вместе с невнимательностью, часто становится головной болью новичка.
    </p>
  </section>
))
